import { z } from "zod";

export type ProjectType = "revlv_project" | "royalty_project";

export const PROJECT_TYPE_LABELS: Record<ProjectType, string> = {
	revlv_project: "Revlv Project",
	royalty_project: "Royalty Project",
};

export const PROJECT_TYPE_OPTIONS = [
	{ value: "revlv_project" as const, label: "Revlv Project" },
	{ value: "royalty_project" as const, label: "Royalty Project" },
];

export type ContractType = "hardware" | "software" | "hardware_software" | "licenses" | "managed_services" | "internet_connectivity";

export const CONTRACT_TYPE_LABELS: Record<ContractType, string> = {
	hardware: "Hardware",
	software: "Software",
	hardware_software: "Hardware + Software",
	licenses: "Licenses",
	managed_services: "Managed Services",
	internet_connectivity: "Internet Connectivity",
};

export const CONTRACT_TYPE_OPTIONS = [
	{ value: "hardware" as const, label: "Hardware" },
	{ value: "software" as const, label: "Software" },
	{ value: "hardware_software" as const, label: "Hardware + Software" },
	{ value: "licenses" as const, label: "Licenses" },
	{ value: "managed_services" as const, label: "Managed Services" },
	{ value: "internet_connectivity" as const, label: "Internet Connectivity" },
];

export const salesContractSchema = z
	.object({
		id: z.string().optional(),
		project_id: z.string().min(1, "Project is required"),
		contract_amount: z.number().min(0, "Contract amount must be positive"),
		monthly_recurring_sales: z.number().min(0, "Monthly recurring sales must be positive"),
		original_monthly_recurring_sales: z.number().min(0, "Monthly recurring sales must be positive"),
		original_contract_amount: z.number().min(0, "Monthly recurring sales must be positive"),
		payment_type: z.enum(["monthly", "quarterly", "annually", "semi-annual"]).optional().nullable(),
		project_type: z.enum(["revlv_project", "royalty_project"]).default("revlv_project"),
		project_category: z.enum(["one-time", "recurring", "recurring-one-time"]).optional().nullable(),
		royalty_amount: z.number().nullable().optional(),
		number_of_invoices: z.number().min(1, "Number of invoices must be at least 1").max(100, "Number of invoices cannot exceed 100"),
		start_date: z.string().min(1, "Start date is required"),
		end_date: z.string().min(1, "End date is required"),
		notes: z.string().optional(),
		tags: z.array(z.string()).optional().nullable(),
		contract_type: z.enum(["hardware", "software", "hardware_software", "licenses", "managed_services", "internet_connectivity"]).optional().nullable(),
		is_vatable: z.boolean().optional().nullable(),
	})
	.refine(
		(data) => {
			if (data.start_date && data.end_date) {
				return new Date(data.end_date) > new Date(data.start_date);
			}
			return true;
		},
		{
			message: "End date must be after start date",
			path: ["end_date"],
		},
	)
	.refine(
		(data) => {
			const ceiling =
				data.project_type === "royalty_project" ? data.original_contract_amount : data.contract_amount;
			return data.monthly_recurring_sales <= ceiling;
		},
		{
			message: "Monthly recurring sales cannot be greater than contract amount",
			path: ["monthly_recurring_sales"],
		},
	);

export type SalesContractFormData = z.infer<typeof salesContractSchema>;

export interface SalesContract {
	id: string;
	project_id: string;
	project: {
		id: string;
		name: string;
		client?: {
			id: string;
			name: string;
			company: string | null;
			email: string | null;
		};
	};
	contract_amount: number;
	monthly_recurring_sales: number;
	payment_type: "monthly" | "quarterly" | "annually" | "semi-annual" | null;
	project_type: ProjectType;
	project_category?: "one-time" | "recurring" | "recurring-one-time" | null;
	contract_type?: ContractType | null;
	royalty_amount: number | null;
	original_contract_amount: number | null;
	original_monthly_recurring_sales: number | null;
	number_of_invoices: number;
	start_date: string;
	end_date: string;
	is_extended: boolean;
	extended_end_date: string | null;
	effective_end_date: string;
	status: "active" | "expired" | "cancelled" | "completed";
	is_vatable: boolean | null;
	salesperson_id: string;
	salesperson: {
		id: string;
		name: string;
		email: string;
	};
	notes: string | null;
	tags: string[] | null;
	is_active: boolean;
	months_remaining: number;
	created_at: string;
	updated_at: string;
}

export interface SalesContractFilters {
	status?: "active" | "expired" | "cancelled" | "completed";
	project_type?: ProjectType;
	contract_type?: ContractType;
	salesperson_id?: string;
	project_id?: string;
	start_date?: string;
	end_date?: string;
	expiring_soon?: number;
	search?: string;
	sort_by?: string;
	sort_order?: "asc" | "desc";
	per_page?: number;
	page?: number;
}

export interface SalesContractDashboard {
	total_monthly_recurring_sales: number;
	total_monthly?: number;
	total_contract_value: number;
	total_collectible: number;
	total_revenue: number;
	total_royalty_amount: number;
	active_contracts_count: number;
	projects_count?: number;
	collected_amount: number;
	pending_collection: number;
	sales_by_salesperson: {
		salesperson: {
			id: string;
			name: string;
			email: string;
		};
		total_contracts: number;
		total_monthly_sales: number;
		total_contract_value: number;
		total_royalty_amount: number;
	}[];
	expiring_soon: SalesContract[];
	expiring_soon_count: number;
	expired: SalesContract[];
	expired_count: number;
	monthly_trend: {
		month: string;
		sales: number;
		collected: number;
		pending: number;
	}[];
}

export interface SalesContractCalendarEvent {
	id: string;
	title: string;
	start: string;
	allDay: boolean;
	color: string;
	contract_id: string;
	type: "start" | "end";
	contract: SalesContract;
}
